import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BotPrompt } from './BotPrompt';
import type { Message, PendingMessage } from '../api/types';

interface Props {
  /** Смена чата — сигнал показать конец переписки, а не то место, где были в прошлом. */
  chatId: string;
  messages: Message[];
  pending: PendingMessage[];
  currentUserId: string;
  showAuthors: boolean;
  authorName: (userId: string | null) => string;
  /** До какого номера сообщение прочитал хотя бы кто-то из собеседников. */
  readSeq: number;
  onReachTop: () => void;
  onDelete: (messageId: string) => void;
  onReply: (message: Message) => void;
  onReact: (messageId: string, emoji: string) => void;
  /** Ответ на вопрос бота — уходит обычным сообщением, как если бы его набрали. */
  onAnswerBot: (value: string) => void;
  historyExhausted: boolean;
}

const NEAR_BOTTOM_PX = 120;

/** Тот же набор, что разрешён на сервере. */
const EMOJI = ['👍', '❤️', '😂', '😮', '😢', '🙏', '🔥', '👏'];

export function MessageList({
  chatId, messages, pending, currentUserId, showAuthors, authorName, readSeq,
  onReachTop, onDelete, onReply, onReact, onAnswerBot, historyExhausted,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const wasNearBottom = useRef(true);
  const previousHeight = useRef(0);
  const previousFirstId = useRef<string | undefined>(undefined);
  const previousPendingCount = useRef(0);
  const previousChatId = useRef(chatId);
  /** Чат только что открыли и до последних сообщений ещё не долистали. */
  const openedChat = useRef(true);
  /** Когда открыли чат: первые секунды лента должна держаться конца переписки. */
  const chatOpenedAt = useRef(Date.now());
  /** Для какого сообщения открыто меню действий. */
  const [openFor, setOpenFor] = useState<string | null>(null);
  const longPressTimer = useRef<number | null>(null);
  /** Когда меню открылось: нажатие, которым его вызвали, не должно его же закрыть. */
  const openedAt = useRef(0);

  /**
   * Меню всегда открываем, а не переключаем.
   *
   * В приложении долгое нажатие вызывало сразу оба пути: сначала срабатывал
   * наш таймер, затем система присылала событие контекстного меню — и второй
   * вызов закрывал только что открытое меню. Со стороны это выглядело как
   * мигание. В браузере на телефоне события контекстного меню нет, поэтому
   * там всё работало.
   */
  function openMenu(messageId: string) {
    cancelLongPress();
    openedAt.current = Date.now();
    setOpenFor(messageId);
  }

  /** Долгое нажатие — привычный жест на телефоне; в iOS события меню нет, нужен таймер. */
  function startLongPress(messageId: string) {
    cancelLongPress();
    longPressTimer.current = window.setTimeout(() => openMenu(messageId), 450);
  }

  function cancelLongPress() {
    if (longPressTimer.current !== null) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }

  /** Закрытие по щелчку мимо и по Esc — привычно для контекстного меню. */
  function closeMenu() {
    // Событие нажатия, открывшего меню, доходит до подложки следом за ним:
    // без этой задержки меню закрывалось бы в тот же миг
    if (Date.now() - openedAt.current < 400) return;
    setOpenFor(null);
  }

  useEffect(() => {
    if (!openFor) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpenFor(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openFor]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    wasNearBottom.current = distance < NEAR_BOTTOM_PX;
  });

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    // Открыли другой чат — показываем конец переписки. Историю догружают
    // отдельным запросом, поэтому первый заход сюда приходится на пустую
    // ленту: признак держим до появления сообщений, иначе прокручивать нечего
    // и человек остаётся в начале переписки.
    if (chatId !== previousChatId.current) {
      previousChatId.current = chatId;
      previousFirstId.current = undefined;
      openedChat.current = true;
      chatOpenedAt.current = Date.now();
    }

    const firstId = messages[0]?.id;
    const prependedOlder = previousFirstId.current !== undefined
      && firstId !== previousFirstId.current
      && messages.some((m) => m.id === previousFirstId.current);

    // Своё только что отправленное сообщение показываем всегда, даже если
    // человек читал старую переписку: иначе непонятно, ушло оно или нет
    const justSent = pending.length > previousPendingCount.current;
    previousPendingCount.current = pending.length;

    if (prependedOlder) {
      // Догрузили историю вверх — держим взгляд на том же сообщении,
      // иначе лента прыгает и читать невозможно
      element.scrollTop = element.scrollHeight - previousHeight.current;
    } else if (openedChat.current || wasNearBottom.current || justSent) {
      element.scrollTop = element.scrollHeight;
    }

    if (messages.length > 0) {
      openedChat.current = false;
    }
    previousHeight.current = element.scrollHeight;
    previousFirstId.current = firstId;
  }, [chatId, messages, pending]);

  /**
   * Клавиатура на телефоне уменьшает видимую область, и лента становится ниже.
   * Без этого отправленное сообщение остаётся за нижней границей экрана —
   * человек не видит, что именно отправил.
   */
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const stickToBottom = () => {
      const element = scrollRef.current;
      if (!element) return;
      // Первые секунды после открытия чата держимся конца переписки в любом
      // случае. Переход из уведомления происходит с уже открытой клавиатурой:
      // область уменьшается после того, как лента отрисовалась, признак
      // «человек был внизу» к этому моменту уже сброшен — и чат открывался
      // в самом начале переписки, хотя свежие сообщения далеко внизу.
      const justOpened = Date.now() - chatOpenedAt.current < 2500;
      if (wasNearBottom.current || justOpened) {
        element.scrollTop = element.scrollHeight;
      }
    };
    viewport.addEventListener('resize', stickToBottom);
    return () => viewport.removeEventListener('resize', stickToBottom);
  }, []);

  function onScroll() {
    const element = scrollRef.current;
    if (!element) return;
    if (element.scrollTop < 60 && !historyExhausted) {
      onReachTop();
    }
  }

  // Последний вопрос бота: только у него имеет смысл показывать выбор ответа
  const lastPromptId = [...messages].reverse()
    .find((message) => message.kind === 'BOT_PROMPT')?.id;

  const items: Array<{ key: string; node: React.ReactNode }> = [];
  let lastDay = '';

  for (const message of messages) {
    const day = formatDay(message.createdAt);
    if (day !== lastDay) {
      lastDay = day;
      items.push({ key: 'day-' + day, node: <div className="day">{day}</div> });
    }

    if (message.kind === 'SYSTEM') {
      items.push({ key: message.id, node: <div className="system">{message.body}</div> });
      continue;
    }

    const own = message.senderId === currentUserId;
    const open = openFor === message.id;

    items.push({
      key: message.id,
      node: (
        <div className={'row-msg' + (own ? ' own' : '')}>
          {/*
            Единый способ вызвать действия: правая кнопка на компьютере,
            долгое нажатие на телефоне. Кнопки при наведении убраны — они
            перекрывали текст сообщения.
          */}
          <div
            className={'bubble' + (own ? ' own' : '') + (message.deleted ? ' deleted' : '')}
            onTouchStart={() => !message.deleted && startLongPress(message.id)}
            onTouchEnd={cancelLongPress}
            onTouchMove={cancelLongPress}
            onTouchCancel={cancelLongPress}
            onContextMenu={(event) => {
              // Системное меню выделения текста здесь только мешает
              event.preventDefault();
              if (!message.deleted) openMenu(message.id);
            }}
          >
            {showAuthors && !own && !message.deleted && (
              <span className="author">{authorName(message.senderId)}</span>
            )}

            {message.replyToId && (
              <span className="quote">
                <span className="quote-author">{message.replyToAuthor ?? 'сообщение'}</span>
                {message.replyToText ?? '…'}
              </span>
            )}

            {message.deleted ? 'сообщение удалено' : message.body}

            <span className="meta">
              {formatTime(message.createdAt)}
              {message.editedAt && ' · изменено'}
              {/* Две галочки — прочитали все остальные участники, одна — только отправлено */}
              {own && !message.deleted && <Ticks read={message.seq <= readSeq} />}
            </span>
          </div>

          {/* Выбор показываем только у последнего вопроса: отвечать на тот,
              что был три шага назад, бот всё равно не умеет, а кнопки в старых
              сообщениях сбивали бы с толку */}
          {message.kind === 'BOT_PROMPT' && message.id === lastPromptId && (
            <BotPrompt payload={message.payload} onAnswer={onAnswerBot} />
          )}

          {message.reactions.length > 0 && (
            <div className={'reactions' + (own ? ' own' : '')}>
              {message.reactions.map((reaction) => (
                <button
                  key={reaction.emoji}
                  className={'reaction' + (reaction.userIds.includes(currentUserId) ? ' mine' : '')}
                  onClick={() => onReact(message.id, reaction.emoji)}
                  title={reaction.userIds.includes(currentUserId) ? 'убрать реакцию' : 'поддержать'}
                >
                  {reaction.emoji} {reaction.count}
                </button>
              ))}
            </div>
          )}

          {open && !message.deleted && (
            <div className={'actions' + (own ? ' own' : '')}>
              {EMOJI.map((emoji) => (
                <button
                  key={emoji}
                  className="emoji"
                  onClick={() => { onReact(message.id, emoji); setOpenFor(null); }}
                >
                  {emoji}
                </button>
              ))}
              <button className="ghost" onClick={() => { onReply(message); setOpenFor(null); }}>
                ответить
              </button>
              {/* Выделение текста в пузыре отключено (иначе долгое нажатие
                  вызывает системное меню), поэтому копирование — отдельным
                  действием, иначе текст стало бы не забрать */}
              <button className="ghost" onClick={() => { void copyText(message.body ?? ''); setOpenFor(null); }}>
                копировать
              </button>
              {own && (
                <button className="ghost danger" onClick={() => { onDelete(message.id); setOpenFor(null); }}>
                  удалить
                </button>
              )}
            </div>
          )}
        </div>
      ),
    });
  }

  for (const item of pending) {
    items.push({
      key: 'p-' + item.clientMsgId,
      node: (
        <div className="row-msg own">
          <div className={'bubble own pending' + (item.status === 'failed' ? ' failed' : '')}>
            {item.body}
            <span className="meta">{item.status === 'failed' ? 'не отправлено' : 'отправляется…'}</span>
          </div>
        </div>
      ),
    });
  }

  return (
    <div className="messages" ref={scrollRef} onScroll={onScroll}>
      {historyExhausted && messages.length > 0 && (
        <div className="day">начало переписки</div>
      )}
      {items.map((item) => <div key={item.key} style={{ display: 'contents' }}>{item.node}</div>)}

      {/* Подложка ловит нажатие мимо меню. Своя, а не слушатель на документе:
          в приложении обработчики на документе не получали касаний сквозь
          собственные элементы ленты */}
      {openFor && (
        <div
          className="menu-backdrop"
          onClick={closeMenu}
          onTouchStart={closeMenu}
          onContextMenu={(event) => { event.preventDefault(); closeMenu(); }}
        />
      )}
    </div>
  );
}

/** Копирование текста сообщения: выделение в пузыре отключено. */
async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Разрешения может не быть — молча, отдельное сообщение об ошибке
    // здесь только помешало бы
  }
}

/**
 * Галочки статуса. Нарисованы одним значком с наложением, как в мессенджерах:
 * два текстовых символа «✓✓» стоят слишком далеко друг от друга и читаются
 * как случайные знаки, а не как единый признак.
 */
function Ticks({ read }: { read: boolean }) {
  return (
    <svg
      className={'ticks' + (read ? ' read' : '')}
      viewBox="0 0 18 11" width="17" height="11" role="img"
      aria-label={read ? 'прочитано' : 'отправлено'}
      fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M1.2 6.2 L4.2 9.2 L10 1.8" />
      {read && <path d="M7.4 6.2 L10.4 9.2 L16.2 1.8" />}
    </svg>
  );
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function formatDay(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
  if (sameDay(date, today)) return 'сегодня';
  if (sameDay(date, yesterday)) return 'вчера';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}
